import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PageLoader from '../ui/PageLoader';
import {
  getPassportFlags,
  getPassportVisibilitySettings,
  updatePassportVisibilitySettings,
  type PassportFeatureFlags,
} from '../../services/api/passport';
import { PassportChrome } from './PassportChrome';

const PassportPrivacyPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [passportPrivate, setPassportPrivate] = useState(true);
  const [collectionsPublic, setCollectionsPublic] = useState(false);
  const [badgesPublic, setBadgesPublic] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [data, f] = await Promise.all([
          getPassportVisibilitySettings(),
          getPassportFlags(),
        ]);
        if (cancelled) return;
        setPassportPrivate(data.passport_private);
        setCollectionsPublic(data.collections_public);
        setBadgesPublic(data.badges_public);
        setFlags(f);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load privacy settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await updatePassportVisibilitySettings({
        passport_private: passportPrivate,
        collections_public: collectionsPublic,
        badges_public: badgesPublic,
      });
      setMessage('Privacy settings saved.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save privacy settings');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || !isAuthenticated) {
    return <PageLoader />;
  }

  return (
    <PassportChrome
      active="settings"
      flags={flags}
      width="narrow"
      title={
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-white">
            Passport privacy
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            Your encounter history is private by default. Exact scan locations are never shown
            publicly.
          </p>
        </div>
      }
    >
      {loading ? (
        <div className="rounded-2xl border border-white/[0.08] bg-[#0c0c0c] p-5 space-y-3" aria-busy="true">
          <div className="h-5 w-40 rounded bg-white/10 animate-pulse" />
          <div className="h-12 rounded bg-white/5 animate-pulse" />
        </div>
      ) : (
        <form
          onSubmit={save}
          className="rounded-2xl border border-white/[0.08] bg-[#0c0c0c] p-5 space-y-4"
        >
          {error ? (
            <div className="alert-error text-sm" role="alert">
              {error}
            </div>
          ) : null}
          {message ? (
            <p className="text-sm text-emerald-300" role="status">
              {message}
            </p>
          ) : null}
          <Toggle
            label="Keep Passport private"
            checked={passportPrivate}
            onChange={setPassportPrivate}
          />
          <Toggle
            label="Allow public collection highlights"
            checked={collectionsPublic}
            onChange={setCollectionsPublic}
          />
          <Toggle
            label="Allow public Passport badges"
            checked={badgesPublic}
            onChange={setBadgesPublic}
          />
          <button type="submit" className="btn-primary min-h-[44px]" disabled={saving}>
            {saving ? 'Saving…' : 'Save privacy settings'}
          </button>
        </form>
      )}
    </PassportChrome>
  );
};

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer min-h-11">
      <input
        type="checkbox"
        className="checkbox-control mt-1"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="text-sm text-white leading-snug">{label}</span>
    </label>
  );
}

export default PassportPrivacyPage;
